import React, { useState } from 'react';
import RestrictedAccess from '../RestrictedAccess/RestrictedAccess';
import AIReportModal from '../AIReportModal/AIReportModal';
import {
  fetchAIReport,
  fetchDirectAwardAIReport,
} from '../../services/aiReportsApi';

const PremiumAIReport = ({
  noticeNo,
  type = 'auction', // 'auction' sau 'directAward'
  buttonLabel = 'Generează Raport AI Premium',
  modalTitle = 'Raport AI Premium',
}) => {
  const [aiReport, setAiReport] = useState(null);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [showAIReport, setShowAIReport] = useState(false);
  const [error, setError] = useState(null);

  const handleDownloadAIReport = async () => {
    setAiReportLoading(true);
    setError(null);
    // Întâi verifică localStorage
    const local = window.localStorage.getItem(`aiReport_${noticeNo}`);
    if (local) {
      try {
        const parsed = JSON.parse(local);
        if (parsed && typeof parsed === 'object') {
          setAiReport(parsed);
          setShowAIReport(true);
          setAiReportLoading(false);
          return;
        }
      } catch {}
    }
    // Selectează metoda corectă din aiReportsApi.js
    try {
      let data;
      if (type === 'auction') {
        data = await fetchAIReport(noticeNo);
      } else if (type === 'directAward') {
        data = await fetchDirectAwardAIReport(noticeNo);
      } else {
        throw new Error('Tip necunoscut pentru raport AI');
      }
      setAiReport(data);
      window.localStorage.setItem(`aiReport_${noticeNo}`, JSON.stringify(data));
      setShowAIReport(true);
    } catch (error) {
      setError(
        'A apărut o eroare la generarea raportului AI. Te rugăm să încerci din nou mai târziu sau să contactezi suportul Go4Projects.',
      );
    } finally {
      setAiReportLoading(false);
    }
  };

  return (
    <>
      <section className="bg-gradient-to-br from-yellow-50 via-white to-indigo-50 border-2 border-yellow-300 rounded-2xl shadow-lg p-4 flex flex-col items-start gap-2 col-span-1 min-h-[260px]">
        <div className="flex items-center gap-2 mb-0.5">
          <h2 className="text-xl font-extrabold text-yellow-700 flex items-center gap-2 leading-tight">
            <svg
              className="inline-block text-yellow-500"
              width="22"
              height="22"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                fill="currentColor"
              />
            </svg>
            {modalTitle}
          </h2>
          <span className="ml-1 px-2 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-bold border border-green-200 shadow-sm tracking-wider">
            BETA
          </span>
        </div>
        <p className="text-gray-700 text-sm mb-1 max-w-xl leading-snug">
          <span className="font-semibold text-indigo-700">
            Vrei să iei decizii mai smart, mai rapid?
          </span>{' '}
          Descoperă{' '}
          <span className="font-bold text-yellow-700">Raportul AI Premium</span>
          : sinteza automată a proiectului și procedurii, cu toate datele
          relevante pentru ofertanți, consultanți și decidenți.{' '}
          <span className="font-semibold text-indigo-700">Obții instant:</span>
        </p>
        <ul className="mb-1 space-y-1">
          <li className="flex items-start gap-1 text-sm text-gray-800">
            <svg
              className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>
              <span className="font-semibold">Titlu proiect</span>,{' '}
              <span className="font-semibold">coduri CPV/CAEN/UNICLASS</span>,{' '}
              <span className="font-semibold">domenii principale</span>,{' '}
              <span className="font-semibold">cuvinte cheie</span>,{' '}
              <span className="font-semibold">taguri AI</span>
            </span>
          </li>
          <li className="flex items-start gap-1 text-sm text-gray-800">
            <svg
              className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>
              <span className="font-semibold">Durată estimată</span>,{' '}
              <span className="font-semibold">valoare estimată</span>,{' '}
              <span className="font-semibold">tehnologie</span>,{' '}
              <span className="font-semibold">tip construcție</span>,{' '}
              <span className="font-semibold">beneficiar</span>
            </span>
          </li>
          <li className="flex items-start gap-1 text-sm text-gray-800">
            <svg
              className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>
              <span className="font-semibold">Impact mediu</span>,{' '}
              <span className="font-semibold">spațiu verde</span>,{' '}
              <span className="font-semibold">demolări</span>,{' '}
              <span className="font-semibold">infrastructură</span>,{' '}
              <span className="font-semibold">reabilitare</span>
            </span>
          </li>
          <li className="flex items-start gap-1 text-sm text-gray-800">
            <svg
              className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>
              <span className="font-semibold">Informații procedură</span> (tip,
              număr, status, dacă există)
            </span>
          </li>
          <li className="flex items-start gap-1 text-sm text-gray-800">
            <svg
              className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span>
              <span className="font-semibold">Experți COR</span> implicați
            </span>
          </li>
        </ul>
        <RestrictedAccess message="Funcționalitate premium disponibilă doar pentru utilizatorii autentificați">
          <button
            onClick={handleDownloadAIReport}
            disabled={aiReportLoading}
            className={`mt-1 px-6 py-2 rounded-xl text-base font-extrabold shadow-lg transition w-full md:w-auto ${aiReportLoading ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-indigo-500 text-white hover:from-yellow-500 hover:to-indigo-600'}`}
          >
            {aiReportLoading ? (
              <span className="flex items-center gap-2 justify-center">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l5-5-5-5v4A12 12 0 002 12h2z"
                  />
                </svg>
                Generare Raport AI…
              </span>
            ) : aiReport ? (
              'Vezi Raport AI'
            ) : (
              buttonLabel
            )}
          </button>
        </RestrictedAccess>
        {error && (
          <div className="mt-3 w-full bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm flex items-center gap-2">
            <svg
              className="w-5 h-5 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M18.364 5.636l-1.414 1.414A9 9 0 105.636 18.364l1.414-1.414A7 7 0 1118.364 5.636z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v4m0 4h.01"
              />
            </svg>
            <span>{error}</span>
          </div>
        )}
        <span className="text-xs text-yellow-700 mt-1">
          Funcționalitate premium, generată automat pe baza datelor proiectului.{' '}
          <span className="font-bold text-green-700">BETA</span> – pot exista
          erori sau date lipsă.
        </span>
      </section>
      {showAIReport && (
        <AIReportModal
          report={aiReport}
          loading={aiReportLoading}
          onClose={() => {
            setShowAIReport(false);
            setAiReport(null);
          }}
        />
      )}
    </>
  );
};

export default PremiumAIReport;
